interface ClassificationValue {
  label: string,
  value: string,
  items?: ClassificationValue[],
  // child_classifications only occurs in API response objects so attr is in snake case
  child_classifications?: ClassificationValue[],
}

interface Option {
  value: string,
  label: string,
}

const OTHER_VALUE = '__OTHER__';

const SEPARATOR = ' ▸ ';

let iterator: string;

// Recursively looks through the provided classification set and constructs
// a single level array of Option objects. If allowOther is true one
// OTHER_VALUE will be included in the array for each level of the
// original classification set.
function childOptions(
  classificationSet: ClassificationValue[],
  allowOther: boolean,
  parentValues: string[] = [],
  parentLabels: string[] = [],
  previousRefMap: string[] = [],
): [Option[], string[]] {
  const options: Option[] = [];
  let referenceMap = previousRefMap;

  function addOption(
    values: string[],
    labels: string[],
  ) {
    const value = JSON.stringify(values);

    // Only add the option to the list if the value is not in the ref map
    if (!referenceMap.includes(value)) {
      referenceMap.push(value);

      options.push({
        label: labels.join(SEPARATOR),
        value,
      });
    }
  }

  classificationSet.forEach((set: ClassificationValue) => {
    // Create a complete list of the values for this classification
    const values = [...parentValues, set.value];
    const labels = [...parentLabels, set.label];

    if (set[iterator]?.length) {
      // Make sure we travel down each branch
      let newOptions: Option[] = [];
      // Get the options and updated reference map for this branch
      [newOptions, referenceMap] = childOptions(
        set[iterator], allowOther, values, labels, previousRefMap,
      );
      // Update the options array with the branches options
      options.push(...newOptions);
    } else {
      // Create the value for a leaf node. Should be a stringified array like
      // ["tier 1", "tier2", "tier3"], for a top level node ["tier 1"]
      addOption(values, labels);
    }
  });

  // Add the OTHER value to this branch, if enabled
  if (allowOther) {
    addOption([...parentValues, OTHER_VALUE], [...parentLabels, 'Other']);
  }

  return [options, referenceMap];
}

function flattenClassifications(
  classificationSet: ClassificationValue[],
  allowOther: boolean = false,
  childIterator: string,
  emptyLabel: string = '- No value -',
): Option[] {
  const options = [{ label: emptyLabel, value: '' }];
  iterator = childIterator;

  options.push(...childOptions(classificationSet, allowOther)[0]);

  return options;
}

export default flattenClassifications;
